/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import React, { Dispatch, useState, useCallback } from "react";

import { useDropzone } from "react-dropzone";
import styled from "styled-components";
import { v4 as uuid } from "uuid";

import { useCurrentUser } from "src/app/useCurrentUser";
import { FileUtils } from "src/classes/utilities/FileUtils";
import { AvatarDropArea, AvatarWrapper, DropZoneIconWrapper } from "src/components/Profile/AvatarDropArea";
import { Avatar } from "src/components/Shared/User/Avatar";
import { Alert } from "src/components/UI/Alert/Alert";
import { Button } from "src/components/UI/Buttons/ButtonPrimitives";
import { EditProfileInput } from "src/components/UI/Form";
import { AddPhotoIcon } from "src/components/UI/Icons";
import { Modal } from "src/components/UI/Modal/Modal";
import { StyledForm, H2 } from "src/components/UI/Modal/style";
import { AVATAR_BUCKET } from "src/lib/constants";

interface Props {
  setEditModalOpen: Dispatch<React.SetStateAction<boolean>>;
}
export default function EditProfile({ setEditModalOpen }: Props) {
  const currentUser = useCurrentUser()!;
  const [bioLength, setBioLength] = useState(currentUser.snapshot.bio.length);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [previewURL, setPreviewURL] = useState<string | undefined>(undefined);
  const [dropRejectedAlertOpen, setDropRejectedAlertOpen] = useState(false);

  const [bio, setBio] = useState(currentUser.snapshot.bio);
  const [displayName, setDisplayName] = useState(currentUser.snapshot.displayName);

  const onDropAccepted = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;
    const renamedFile = FileUtils.renameFile(file, uuid());
    setAvatarFile(renamedFile);
    const url = await FileUtils.readURLFromFile(renamedFile);
    setPreviewURL(url);
  }, []);

  // hook into dropzone
  const { getRootProps, getInputProps } = useDropzone({
    onDropRejected: () => {
      setDropRejectedAlertOpen(true);
    },
    onDropAccepted,
    multiple: false,
    accept: {
      "image/*": [".png", ".gif", ".jpeg", ".jpg"],
    },
  });

  const handleBioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBioLength(e.target.value?.length || 0);
    setBio(e.target.value);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    if (!currentUser) return;
    e.preventDefault();
    let avatarName = currentUser.snapshot.avatarName;
    if (avatarFile) {
      avatarName = avatarFile.name;
      await FileUtils.uploadFile(avatarFile, AVATAR_BUCKET);
    }
    await currentUser.saveProfileChanges({ bio, displayName, avatarName });
    setEditModalOpen(false);
  };

  return (
    <Modal close={() => setEditModalOpen(false)} title="Edit Profile">
      <AvatarDropArea acceptedFile={!!avatarFile} getRootProps={getRootProps}>
        <input {...getInputProps()} type="file" name="file" />
        <AvatarWrapper>
          <Avatar size={75} src={currentUser.avatarURL(75)} previewAvatar={previewURL} />
        </AvatarWrapper>
        <DropZoneIconWrapper>
          <AddPhotoIcon />
        </DropZoneIconWrapper>
        <Alert
          type="notification"
          okayText="OK"
          onOkay={() => setDropRejectedAlertOpen(false)}
          isOpen={dropRejectedAlertOpen}
          headerText="You must upload a single image file"
        />
      </AvatarDropArea>
      <StyledForm style={{ marginTop: "20px" }} onSubmit={(e) => handleSubmit(e)}>
        <H2>Name</H2>
        <EditProfileInput
          type="text"
          maxLength={35}
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          className="form-control"
        />
        <H2>Bio</H2>
        <EditProfileInput
          type="text"
          maxLength={140}
          value={bio}
          onChange={handleBioChange}
          className="form-control"
          style={{ marginBottom: "5px" }}
        />
        <BioLength>{`${bioLength} / 140`}</BioLength>
        <Button
          as="input"
          style={{ marginTop: "20px", marginBottom: "40px" }}
          type="submit"
          value="Save Changes"
        />
      </StyledForm>
    </Modal>
  );
}

const BioLength = styled.div`
  font-family: "Inter";
  font-size: 12px;
  color: #cccccc;
`;
